import { createClient } from '@supabase/supabase-js'

// Environment variables from Vite - Production-ready configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Validate environment variables with clear error messages
if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable. Please check your Netlify environment variables.')
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable. Please check your Netlify environment variables.')
}

// CRITICAL FIX: Storage validation and cleanup for auth conflicts
const validateAndCleanStorage = () => {
  console.log('🧹 Validating auth storage...')
  
  try {
    // Check for corrupted or expired auth tokens
    Object.keys(localStorage).forEach(key => {
      if (key.includes('supabase') && key.includes('auth')) {
        try {
          const value = localStorage.getItem(key)
          if (value) {
            const parsed = JSON.parse(value)
            
            // Check if token is expired or malformed
            if (parsed.expires_at && Date.now() > parsed.expires_at * 1000) {
              console.log('🗑️ Removing expired auth token:', key)
              localStorage.removeItem(key)
            } else if (!parsed.access_token || typeof parsed.access_token !== 'string') {
              console.log('🗑️ Removing malformed auth token:', key)
              localStorage.removeItem(key)
            }
          }
        } catch (e) {
          console.log('🗑️ Removing corrupted auth token:', key)
          localStorage.removeItem(key)
        }
      }
    })
    
    console.log('✅ Auth storage validation complete')
  } catch (error) {
    console.error('❌ Storage validation error:', error)
    // If validation fails, clear everything to be safe
    console.log('🧨 Clearing all storage due to validation error')
    try {
      localStorage.clear()
    } catch (e) {
      console.error('Failed to clear localStorage:', e)
    }
  }
}

// Clean storage before creating client
validateAndCleanStorage()

// Log configuration status for debugging (remove in production)
console.log('Supabase URL:', supabaseUrl)
console.log('Supabase Key present:', !!supabaseAnonKey)

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Additional settings to prevent auth conflicts
    storageKey: `fcfrick-auth-${import.meta.env.MODE}`, // Environment-specific storage
    debug: import.meta.env.DEV, // Debug mode in development
  }
})

// Types für Supabase Tabellen — muss der echten Prod-Schema (Supabase-Projekt
// dmsaltftbtfvmbgvvmee) entsprechen. Jede Table braucht Row/Insert/Update/
// Relationships und `public` braucht Views/Functions, sonst erfüllt das
// GenericSchema-Constraint von @supabase/supabase-js nicht und der Client
// fällt komplett auf `never` zurück (macht jede .from()/.rpc()-Nutzung
// ungetyped — das war die Ursache fast aller TS-Fehler im Rest des Repos).
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          role: 'admin' | 'trainer'
          status: 'pending' | 'approved' | 'rejected'
          name: string | null
          team: string | null
          created_at: string
          updated_at: string
          approved_by: string | null
          approved_at: string | null
        }
        Insert: {
          id: string
          email: string
          role?: 'admin' | 'trainer'
          status?: 'pending' | 'approved' | 'rejected'
          name?: string | null
          team?: string | null
          created_at?: string
          updated_at?: string
          approved_by?: string | null
          approved_at?: string | null
        }
        Update: {
          id?: string
          email?: string
          role?: 'admin' | 'trainer'
          status?: 'pending' | 'approved' | 'rejected'
          name?: string | null
          team?: string | null
          created_at?: string
          updated_at?: string
          approved_by?: string | null
          approved_at?: string | null
        }
        Relationships: []
      }
      orders: {
        Row: {
          id: string
          items: any[]
          status: 'pending' | 'approved' | 'rejected' | 'shipped' | 'delivered' | 'cancelled' | 'archived'
          previous_status: string | null
          order_date: string
          approval_date: string | null
          approver_name: string | null
          approval_comment: string | null
          notes: string | null
          ordered_by: string
          team: string
          trainer_name: string
          delivery_month: 'August' | 'Dezember'
          user_id: string | null
          created_at: string
          updated_at: string
          total_amount: number | null
        }
        Insert: {
          id?: string
          items: any[]
          status?: 'pending' | 'approved' | 'rejected' | 'shipped' | 'delivered' | 'cancelled' | 'archived'
          previous_status?: string | null
          order_date?: string
          approval_date?: string | null
          approver_name?: string | null
          approval_comment?: string | null
          notes?: string | null
          ordered_by: string
          team: string
          trainer_name: string
          delivery_month: 'August' | 'Dezember'
          user_id?: string | null
          created_at?: string
          updated_at?: string
          total_amount?: number | null
        }
        Update: {
          id?: string
          items?: any[]
          status?: 'pending' | 'approved' | 'rejected' | 'shipped' | 'delivered' | 'cancelled' | 'archived'
          previous_status?: string | null
          order_date?: string
          approval_date?: string | null
          approver_name?: string | null
          approval_comment?: string | null
          notes?: string | null
          ordered_by?: string
          team?: string
          trainer_name?: string
          delivery_month?: 'August' | 'Dezember'
          user_id?: string | null
          created_at?: string
          updated_at?: string
          total_amount?: number | null
        }
        Relationships: []
      }
      bus_reservations: {
        Row: {
          id: string
          date: string
          start_time: string
          end_time: string | null
          destination: string
          purpose: string
          driver: string
          contact_phone: string
          team: string
          status: 'pending' | 'approved' | 'rejected'
          notes: string | null
          user_id: string | null
          created_at: string
          updated_at: string
          user_profile: Record<string, unknown> | null
        }
        Insert: {
          id?: string
          date: string
          start_time: string
          end_time?: string | null
          destination: string
          purpose: string
          driver: string
          contact_phone: string
          team: string
          status?: 'pending' | 'approved' | 'rejected'
          notes?: string | null
          user_id?: string | null
          created_at?: string
          updated_at?: string
          user_profile?: Record<string, unknown> | null
        }
        Update: {
          id?: string
          date?: string
          start_time?: string
          end_time?: string | null
          destination?: string
          purpose?: string
          driver?: string
          contact_phone?: string
          team?: string
          status?: 'pending' | 'approved' | 'rejected'
          notes?: string | null
          user_id?: string | null
          created_at?: string
          updated_at?: string
          user_profile?: Record<string, unknown> | null
        }
        Relationships: []
      }
      testspiele: {
        Row: {
          id: string
          team: string | null
          opponent: string | null
          date: string
          time: string
          trainer_name: string | null
          location: string | null
          is_home: boolean | null
          notes: string | null
          status: 'available' | 'pending' | 'confirmed' | 'cancelled'
          contact_person: string | null
          contact_email: string | null
          contact_phone: string | null
          user_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          team?: string | null
          opponent?: string | null
          date: string
          time: string
          trainer_name?: string | null
          location?: string | null
          is_home?: boolean | null
          notes?: string | null
          status?: 'available' | 'pending' | 'confirmed' | 'cancelled'
          contact_person?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          user_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          team?: string | null
          opponent?: string | null
          date?: string
          time?: string
          trainer_name?: string | null
          location?: string | null
          is_home?: boolean | null
          notes?: string | null
          status?: 'available' | 'pending' | 'confirmed' | 'cancelled'
          contact_person?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          user_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      equipment: {
        Row: {
          id: string
          name: string
          description: string
          category: string
          price: number | null
          sizes: string[] | null
          colors: string[] | null
          image: string | null
          in_stock: boolean | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description: string
          category: string
          price?: number | null
          sizes?: string[] | null
          colors?: string[] | null
          image?: string | null
          in_stock?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string
          category?: string
          price?: number | null
          sizes?: string[] | null
          colors?: string[] | null
          image?: string | null
          in_stock?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      equipment_categories: {
        Row: {
          id: string
          name: string
          description: string | null
          order_index: number | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          order_index?: number | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          order_index?: number | null
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      files: {
        Row: {
          id: string
          uploaded_at: string
          original_name: string
          file_name: string
          file_path: string
          file_size: number
          file_type: string
          category: 'Trainingsplan' | 'Formulare' | 'Vereinsdokumente' | 'Sitzungsprotokolle' | 'Andere'
          description: string | null
          uploaded_by: string
          public_url: string | null
          updated_at: string
          status: 'active' | 'deleted' | null
          downloadurl: string | null
          supabasestoragepath: string | null
        }
        Insert: {
          id?: string
          uploaded_at?: string
          original_name: string
          file_name: string
          file_path: string
          file_size: number
          file_type: string
          category: 'Trainingsplan' | 'Formulare' | 'Vereinsdokumente' | 'Sitzungsprotokolle' | 'Andere'
          description?: string | null
          uploaded_by: string
          public_url?: string | null
          updated_at?: string
          status?: 'active' | 'deleted' | null
          downloadurl?: string | null
          supabasestoragepath?: string | null
        }
        Update: {
          id?: string
          uploaded_at?: string
          original_name?: string
          file_name?: string
          file_path?: string
          file_size?: number
          file_type?: string
          category?: 'Trainingsplan' | 'Formulare' | 'Vereinsdokumente' | 'Sitzungsprotokolle' | 'Andere'
          description?: string | null
          uploaded_by?: string
          public_url?: string | null
          updated_at?: string
          status?: 'active' | 'deleted' | null
          downloadurl?: string | null
          supabasestoragepath?: string | null
        }
        Relationships: []
      }
      game_date_slots: {
        Row: {
          id: string
          game_date: string
          start_time: string
          end_time: string
          team_category: string
          age_group: string | null
          day_of_week: string
          slot_description: string | null
          special_notes: string | null
          created_at: string
          updated_at: string
          created_by: string | null
          reserved_by: string | null
          status: 'available' | 'reserved' | 'occupied' | 'cancelled'
        }
        Insert: {
          id?: string
          game_date: string
          start_time: string
          end_time: string
          team_category: string
          age_group?: string | null
          day_of_week: string
          slot_description?: string | null
          special_notes?: string | null
          created_at?: string
          updated_at?: string
          created_by?: string | null
          reserved_by?: string | null
          status?: 'available' | 'reserved' | 'occupied' | 'cancelled'
        }
        Update: {
          id?: string
          game_date?: string
          start_time?: string
          end_time?: string
          team_category?: string
          age_group?: string | null
          day_of_week?: string
          slot_description?: string | null
          special_notes?: string | null
          created_at?: string
          updated_at?: string
          created_by?: string | null
          reserved_by?: string | null
          status?: 'available' | 'reserved' | 'occupied' | 'cancelled'
        }
        Relationships: []
      }
      cards: {
        Row: {
          id: string
          serial: string
          holder_name: string
          season: string | null
          status: string
          created_at: string
          updated_at: string
          email: string | null
          foxi_id: string | null
          foxi_url: string | null
        }
        Insert: {
          id?: string
          serial: string
          holder_name: string
          season?: string | null
          status?: string
          created_at?: string
          updated_at?: string
          email?: string | null
          foxi_id?: string | null
          foxi_url?: string | null
        }
        Update: {
          id?: string
          serial?: string
          holder_name?: string
          season?: string | null
          status?: string
          created_at?: string
          updated_at?: string
          email?: string | null
          foxi_id?: string | null
          foxi_url?: string | null
        }
        Relationships: []
      }
      support_tickets: {
        Row: {
          id: string
          title: string
          body: string
          status: 'open' | 'in_progress' | 'done'
          owner_id: string
          // Kategorie-Key -> support_categories. Nullable: Altbestand aus 002 und
          // Clients mit altem Service Worker schicken keine Kategorie mit.
          category: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          body?: string
          status?: 'open' | 'in_progress' | 'done'
          owner_id: string
          category?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          body?: string
          status?: 'open' | 'in_progress' | 'done'
          owner_id?: string
          category?: string | null
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      // Kategorie -> Vereinspostfach. Nur lesbar (RLS, freigeschaltete Mitglieder),
      // niemals vom Client beschreibbar. Gepflegt ueber das Supabase-Dashboard.
      support_categories: {
        Row: {
          key: string
          label: string
          email: string
          sort_order: number
        }
        Insert: {
          key: string
          label: string
          email: string
          sort_order?: number
        }
        Update: {
          key?: string
          label?: string
          email?: string
          sort_order?: number
        }
        Relationships: []
      }
    }
    Views: Record<string, never>
    Functions: {
      get_team_slots: {
        Args: { team_name: string }
        Returns: {
          slot_id: string
          slot_date: string
          slot_time: string
          slot_status: 'available' | 'pending' | 'confirmed' | 'cancelled'
          team_value: string | null
          opponent: string | null
          trainer_name: string | null
        }[]
      }
      get_all_slots: {
        Args: Record<PropertyKey, never>
        Returns: {
          slot_id: string
          slot_date: string
          slot_time: string
          slot_status: 'available' | 'pending' | 'confirmed' | 'cancelled'
          team_value: string | null
          opponent: string | null
          trainer_name: string | null
        }[]
      }
    }
  }
}
